import type { ReactNode } from "react";
import InquiryFormSection from "../components/InquiryFormSection";
import companyCtaBackgroundImage from "../assets/company-cta-bg.jpg";
import companyChongqingImage from "../assets/company-chongqing.jpg";
import companyContactFormImage from "../assets/company-contact-form.png";
import companyGreatWallImage from "../assets/company-great-wall.jpg";
import companyJiuzhaigouImage from "../assets/company-jiuzhaigou.jpg";
import companyPotalaPalaceImage from "../assets/company-potala-palace.jpg";
import companyTopBannerImage from "../assets/company-top-banner.png";

type CompanyReason = {
  title: string;
  icon: ReactNode;
  items: Array<{
    label: string;
    text: string;
  }>;
};

type CompanyProcessStep = {
  title: string;
  description: string;
};

type CompanyTabProps = {
  companyReasons: CompanyReason[];
  companyProcessSteps: CompanyProcessStep[];
  companyPartners: string[];
  apiBaseUrl: string;
  turnstileSiteKey?: string;
  onContactUs: () => void;
};

export default function CompanyTab({
  companyReasons,
  companyProcessSteps,
  companyPartners,
  apiBaseUrl,
  turnstileSiteKey,
  onContactUs
}: CompanyTabProps) {
  return (
    <section className="mb-10 space-y-12 md:mb-16">
      <div className="relative h-[260px] overflow-hidden md:h-[340px]">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('${companyTopBannerImage}')` }}
        />
        <div className="absolute inset-0 bg-[rgba(0,0,0,0.45)]" />
        <div className="relative z-10 flex h-full items-center justify-center px-6 text-center">
          <h2 className="text-3xl font-semibold text-white md:text-5xl">
            About Us
          </h2>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1140px] px-4 md:px-8 lg:px-10">
        <div className="grid items-center gap-12 lg:grid-cols-[1.1fr_0.9fr]">
          <div>
            <p className="text-sm font-semibold text-[#f3b455]">Who We Are?</p>
            <h3 className="mt-4 text-3xl font-semibold leading-tight text-[#111827] md:text-4xl">
              Trip Planning for the Best Travel Experience Possible
            </h3>
            <p className="mt-5 text-sm leading-relaxed text-[#6b7280]">
              We are Fulcrum Travel, a team that creates personalized and
              high-quality travel experiences for foreign visitors. We know that
              group tours are rigid, and planning a trip yourself is
              complicated. That&apos;s why we offer private, tailor-made tours -
              we design a unique itinerary based on your interests, schedule,
              and budget. Whether you are traveling with family, for business,
              or on a personal adventure, our goal is simple: to make your trip
              to China easy, comfortable, and truly memorable.
            </p>
          </div>

          <div className="relative mx-auto w-full max-w-[420px]">
            <div className="grid grid-cols-[1fr_1fr] gap-6">
              <div className="space-y-6">
                <img
                  src={companyGreatWallImage}
                  alt="Great Wall view"
                  className="h-[220px] w-full rounded-2xl object-cover shadow-[0_18px_40px_rgba(15,23,36,0.12)]"
                />
                <img
                  src={companyPotalaPalaceImage}
                  alt="Potala Palace view"
                  className="h-[180px] w-full rounded-2xl object-cover shadow-[0_18px_40px_rgba(15,23,36,0.12)]"
                />
              </div>
              <div className="space-y-6 pt-10">
                <img
                  src={companyChongqingImage}
                  alt="Chongqing skyline"
                  className="h-[220px] w-full rounded-2xl object-cover shadow-[0_18px_40px_rgba(15,23,36,0.12)]"
                />
                <img
                  src={companyJiuzhaigouImage}
                  alt="Jiuzhaigou view"
                  className="h-[180px] w-full rounded-2xl object-cover shadow-[0_18px_40px_rgba(15,23,36,0.12)]"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1140px] px-4 md:px-8 lg:px-10">
        <div className="text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.3em] text-[#f3b455]">
            Why Choose Us?
          </p>
          <h3 className="mt-4 text-3xl font-semibold text-[#111827] md:text-4xl">
            Perfect Vacation come True
          </h3>
        </div>

        <div className="mt-10 grid gap-12 md:grid-cols-2">
          {companyReasons.map((reason) => (
            <div key={reason.title} className="text-center">
              <div className="flex items-center justify-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#fff7e2]">
                  {reason.icon}
                </span>
              </div>
              <h4 className="mt-5 text-lg font-semibold text-[#111827]">
                {reason.title}
              </h4>
              <div className="mt-4 space-y-3 text-left text-sm leading-relaxed text-[#6b7280]">
                {reason.items.map((item) => (
                  <p key={item.label}>
                    <span className="font-semibold text-[#111827]">
                      {item.label}
                    </span>{" "}
                    {item.text}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="relative overflow-hidden">
        <div
          className="h-[260px] w-full bg-cover bg-center md:h-[320px]"
          style={{ backgroundImage: `url('${companyCtaBackgroundImage}')` }}
        />
        <div className="absolute inset-0 bg-[rgba(6,10,16,0.55)]" />
        <div className="absolute inset-0 flex items-center justify-center px-6 text-center">
          <div className="max-w-2xl text-white">
            <h3 className="text-3xl font-semibold md:text-4xl">
              Let us Help you Plan your Next Getaway Now
            </h3>
            <p className="mt-4 text-sm text-white/80 md:text-base">
              Contact our agency and we will get you a quote in a very short
              time. Discover the world with us and book your next unforgettable
              adventure today.
            </p>
            <button
              type="button"
              onClick={onContactUs}
              className="mt-6 rounded-full bg-[#f3b455] px-6 py-2 text-sm font-semibold text-[#1d1d1d] shadow-[0_12px_28px_rgba(243,180,85,0.35)]"
            >
              Contact Us
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1140px] px-4 md:px-8 lg:px-10">
        <div className="grid gap-12 lg:grid-cols-[1fr_0.9fr]">
          <div className="rounded-2xl border border-[#eef2f6] bg-white">
            {companyProcessSteps.map((step, index) => (
              <div
                key={step.title}
                className={`px-6 py-5 text-sm text-[#5f6b7a] ${
                  index === companyProcessSteps.length - 1
                    ? ""
                    : "border-b border-[#eef2f6]"
                }`}
              >
                <span className="font-semibold text-[#111827]">
                  {step.title}
                </span>{" "}
                - {step.description}
              </div>
            ))}
          </div>

          <div className="flex flex-col justify-center">
            <p className="text-sm font-semibold text-[#f3b455]">
              Always by your side
            </p>
            <h3 className="mt-4 text-3xl font-semibold leading-tight text-[#111827] md:text-4xl">
              How can we create a truly high-end customized tour for you?
            </h3>
            <p className="mt-5 text-sm leading-relaxed text-[#6b7280]">
              Our team of experienced travel experts provides personalized
              recommendations and advice to create a trip that suits your
              preferences and budget. With our knowledge and expertise in the
              travel industry, we ensure that all aspects of your trip are
              thoughtfully planned.
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1140px] px-4 pb-6 md:px-8 lg:px-10">
        <div className="text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.3em] text-[#f3b455]">
            Personalized Trips
          </p>
          <h3 className="mt-4 text-3xl font-semibold text-[#111827] md:text-4xl">
            Contact us for a Quote
          </h3>
        </div>
        <InquiryFormSection
          apiBaseUrl={apiBaseUrl}
          turnstileSiteKey={turnstileSiteKey}
          source="company"
          imageSrc={companyContactFormImage}
          imageAlt="Travel inspiration"
          partners={companyPartners}
        />
      </div>
    </section>
  );
}
